import {
  Procedure,
  CodeableConcept,
  Patient,
  Encounter,
} from '@medplum/fhirtypes';
import { createReference } from '@medplum/core';
import { SYSTEMS } from '@consts/systems.consts';
import {
  THERAPEUTIC_SERVICE_SYSTEM,
  DEFAULT_STATUS,
} from '@consts/therapeutic-service.consts';
import { calculateAgeAtGivenTime } from './calculate-age-at-given-time';

// =============================================================================
// GETTERS - Safe access to nested fields
// =============================================================================

export function getServiceCode(proc: Procedure): CodeableConcept | undefined {
  return proc.code;
}

export function getServiceName(proc: Procedure): string | undefined {
  const code = proc.code;
  return code?.text || code?.coding?.[0]?.display;
}

export function getStartAge(proc: Procedure): number | undefined {
  return proc.performedAge?.value;
}

export function getStartDate(proc: Procedure): Date | null {
  const dateStr = proc.performedPeriod?.start;
  return dateStr ? new Date(dateStr) : null;
}

export function getEndDate(proc: Procedure): Date | null {
  const dateStr = proc.performedPeriod?.end;
  return dateStr ? new Date(dateStr) : null;
}

export function getResponse(proc: Procedure): string | undefined {
  return proc.outcome?.text;
}

export function getContextEncounterId(proc: Procedure): string | undefined {
  return proc.encounter?.reference?.replace('Encounter/', '');
}

// =============================================================================
// SETTERS - Immutable updates returning new Procedure
// =============================================================================

export function setServiceCode(
  proc: Procedure,
  code: CodeableConcept | undefined
): Procedure {
  return { ...proc, code };
}

export function setStartAge(
  proc: Procedure,
  age: number | undefined
): Procedure {
  if (age === undefined || age < 0) {
    // Remove performedAge if undefined or invalid
    const { performedAge: _performedAge, ...rest } = proc;
    return rest;
  }

  return {
    ...proc,
    performedAge: {
      value: age,
      unit: 'years',
      system: SYSTEMS.UCUM,
      code: 'a',
    },
  };
}

export function setStartDate(proc: Procedure, date: Date | null): Procedure {
  if (!date) {
    // Remove performedPeriod if no start date
    const { performedPeriod: _performedPeriod, ...rest } = proc;
    return rest;
  }

  return {
    ...proc,
    performedPeriod: {
      ...proc.performedPeriod,
      start: date.toISOString(),
    },
  };
}

export function setEndDate(proc: Procedure, date: Date | null): Procedure {
  if (!date) {
    // Remove end date but keep period if start exists
    if (proc.performedPeriod?.start) {
      const { end: _end, ...restPeriod } = proc.performedPeriod;
      return {
        ...proc,
        performedPeriod: restPeriod,
      };
    }
    // Remove entire period if no start date
    const { performedPeriod: _performedPeriod, ...rest } = proc;
    return rest;
  }

  return {
    ...proc,
    performedPeriod: {
      ...proc.performedPeriod,
      end: date.toISOString(),
    },
  };
}

export function setResponse(
  proc: Procedure,
  response: string | undefined
): Procedure {
  if (!response) {
    const { outcome: _outcome, ...rest } = proc;
    return rest;
  }

  return {
    ...proc,
    outcome: { text: response },
  };
}

export function setStatus(
  proc: Procedure,
  status: Procedure['status']
): Procedure {
  return { ...proc, status };
}

// =============================================================================
// FACTORY - Create new Procedure for therapeutic service
// =============================================================================

export function createNewTherapeuticServiceProcedure(
  patient: Patient,
  encounter: Encounter
): Procedure {
  return {
    resourceType: 'Procedure',
    status: DEFAULT_STATUS,
    subject: createReference(patient),
    encounter: createReference(encounter),
  };
}

// =============================================================================
// HELPERS - Convenience functions
// =============================================================================

/**
 * Calculate age at procedure start date
 * @param proc - The Procedure resource
 * @param patient - The Patient resource
 * @returns The age (years, months, days) at start date, or undefined if dates are not available
 */
export function calculateAgeAtStart(
  proc: Procedure,
  patient: Patient
): { years: number; months: number; days: number } | undefined {
  const startDate = getStartDate(proc);
  if (!startDate || !patient.birthDate) {
    return undefined;
  }

  try {
    const birthDate = new Date(patient.birthDate);
    return calculateAgeAtGivenTime(birthDate, startDate);
  } catch {
    return undefined; // Handle invalid dates gracefully
  }
}

export function isTherapeuticService(proc: Procedure): boolean {
  return Boolean(
    proc.code?.coding?.some(
      (coding) => coding.system === THERAPEUTIC_SERVICE_SYSTEM && coding.code
    )
  );
}

export function hasServiceCode(proc: Procedure): boolean {
  const code = proc.code;
  return Boolean(code?.coding?.[0]?.code || code?.text);
}

export function isStopped(proc: Procedure): boolean {
  return proc.status === 'stopped';
}

/**
 * Prepare a procedure for saving to the server.
 * Validates and ensures all required fields are properly set.
 * @param proc - The Procedure resource to prepare
 * @returns The validated and cleaned Procedure resource
 */
export function prepareProcedureForSave(proc: Procedure): Procedure {
  let prepared = { ...proc };

  // Validate and clean performedAge (for legacy age-based records)
  const startAge = getStartAge(prepared);
  if (startAge !== undefined && startAge < 0) {
    // Remove invalid age
    prepared = setStartAge(prepared, undefined);
  }

  // Validate date period
  const startDate = getStartDate(prepared);
  const endDate = getEndDate(prepared);
  if (startDate && endDate && endDate < startDate) {
    // Invalid date range - remove end date
    prepared = setEndDate(prepared, null);
  }

  return prepared;
}
